import type { DuplicationStats } from '../../types';

interface DuplicationCardProps {
  stats: DuplicationStats;
  title?: string;
}

function percentTone(percent: number): string {
  if (percent >= 20) return 'text-[#F0544F]';
  if (percent >= 5) return 'text-amber-600';
  return 'text-[#70B77E]';
}

function describe(stats: DuplicationStats): string {
  const { distinctScans, duplicatedScans, submissions, maxSubmissions } = stats;
  if (distinctScans === 0) {
    return 'No scans in this period.';
  }
  if (duplicatedScans === 0) {
    return `Each of the ${distinctScans} scan IDs in this period was submitted once.`;
  }
  return `${duplicatedScans} of ${distinctScans} scan IDs arrived more than once — up to ${maxSubmissions} times each — across ${submissions} jobs.`;
}

/**
 * Share of scan IDs that were submitted more than once. Sits alongside the
 * completion and duration panels on every application's Overview.
 */
export function DuplicationCard({ stats, title = 'Duplication' }: DuplicationCardProps) {
  return (
    <div className="bg-white rounded-lg border border-surface-200 p-3 shadow-card">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="w-7 h-7 rounded bg-indigo-100 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </div>
        <h2 className="text-sm font-semibold text-surface-800">{title}</h2>
        <div className="ml-auto flex items-center gap-3">
          <div className="text-center">
            <p className={`text-lg font-bold ${percentTone(stats.duplicatePercent)}`}>
              {stats.duplicatePercent.toFixed(1)}%
            </p>
            <p className="text-[10px] text-surface-500 uppercase">Duplicated</p>
          </div>
          <div className="h-6 w-px bg-surface-200" />
          <div className="text-center">
            <p className="text-lg font-bold text-surface-700">{stats.distinctScans}</p>
            <p className="text-[10px] text-surface-500 uppercase">Scan IDs</p>
          </div>
          <div className="h-6 w-px bg-surface-200" />
          <div className="text-center">
            <p className="text-lg font-bold text-surface-700">{stats.submissions}</p>
            <p className="text-[10px] text-surface-500 uppercase">Submissions</p>
          </div>
        </div>
      </div>

      <div className="mt-2 pt-2 border-t border-surface-100">
        <p className="text-xs text-surface-600">{describe(stats)}</p>
        <p className="text-[10px] text-surface-400 mt-0.5">
          Repeats are counted inside this period only, over jobs that have finished.
        </p>
      </div>
    </div>
  );
}
